import type { ApolloLink } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import * as Sentry from "@sentry/react";
import { SentryLink } from "apollo-link-sentry";
import { toast } from "react-toastify";

import { redactVariables } from "settings/sentry";
import i18n from "translations/i18n";
import { showGraphQLErrorToasts } from "utils/showGraphQLErrorToasts";

import type { TGraphQLError } from "./apollo/types";

export const createErrorReportingLink = ({
  showToasts = true,
  silentCodes = [],
  unreportedCodes = [],
}: { showToasts?: boolean; silentCodes?: string[]; unreportedCodes?: string[] } = {}): ApolloLink =>
  onError(({ operation, graphQLErrors, networkError }) => {
    const mainDefinition = getMainDefinition(operation.query);
    const isMutation = mainDefinition.kind === "OperationDefinition" && mainDefinition.operation === "mutation";
    const toastErrors = (graphQLErrors as readonly TGraphQLError[] | undefined)?.filter(
      ({ code }) => !code || !silentCodes.includes(code),
    );

    if (showToasts && isMutation && toastErrors?.length) {
      showGraphQLErrorToasts(toastErrors);
    }

    if (showToasts && isMutation && networkError) {
      toast.error(i18n.t("errors.something_went_wrong"));
    }

    Sentry.withScope(scope => {
      scope.setTransactionName(operation.operationName);
      scope.setContext("Apollo GraphQL Operation", {
        operationName: operation.operationName,
        variables: JSON.stringify(redactVariables(operation.variables)),
        extensions: operation.extensions,
        query: operation.query.loc?.source.body ?? "",
      });

      graphQLErrors?.forEach((error: TGraphQLError) => {
        if (error.code && unreportedCodes.includes(error.code)) {
          return;
        }

        Sentry.captureMessage(error.message, {
          level: "error",
          fingerprint: ["{{ default }}", operation.operationName],
          contexts: {
            "Apollo GraphQL Error": {
              code: error.code,
              fields: JSON.stringify(error.fields?.map(field => field.message)),
              message: error.message,
            },
          },
        });
      });

      if (networkError) {
        Sentry.captureMessage(networkError.message, {
          level: "error",
          contexts: {
            apolloNetworkError: {
              error: networkError,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              extensions: (networkError as any).extensions,
            },
          },
        });
      }
    });
  });

export const createSentryBreadcrumbLink = (uri: string): ApolloLink =>
  new SentryLink({
    uri,
    setTransaction: false,
    setFingerprint: false,
    attachBreadcrumbs: {
      includeQuery: true,
      includeFetchResult: false,
      includeError: true,
      includeVariables: true,
      transform: breadcrumb => ({
        ...breadcrumb,
        data: {
          ...breadcrumb.data,
          variables: breadcrumb.data.variables && redactVariables(breadcrumb.data.variables),
        },
      }),
    },
  });
